/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import {
    ColumnActionsMode,
    ConstrainMode,
    DetailsRow,
    IColumn,
    IDetailsRowBaseProps,
    IDetailsRowProps
} from '@fluentui/react/lib/DetailsList';
import { IStackProps, Stack } from '@fluentui/react/lib/Stack';
import debounce from 'lodash.debounce';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { Table } from 'src/core/components/Table';
import { TableProps } from 'src/core/components/TableProps';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getStylistV2, important } from 'src/core/themes/luisStylist';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { useIntents } from 'src/luis/modules/intents/useIntents';
import { UtterancePredictionsStore } from 'src/luis/projects/utterances/hooks/useUtterances';
import { LuisId } from 'src/luis/types/Resource';
import { EntityUtteranceTableViewOptions } from 'src/luis/verticals/entityDetails/components/entityExamples/EntityExamples';
import { EntityUtteranceRow } from 'src/luis/verticals/entityDetails/components/entityExamples/EntityUtteranceRow';
import { UtteranceScoreCell } from 'src/luis/verticals/entityDetails/components/entityExamples/UtteranceScoreCell';
import { ResizeProvider, useResizeTrigger } from 'src/modules/labeling/contexts/ResizeContext';

export type EntityUtterancesTableProps = TableProps<UtterancePredictionsStore> & {
    /**
     * Utterance table view options
     */
    viewOptions: EntityUtteranceTableViewOptions;

    /**
     * The id of the utterance being edited.
     */
    utteranceIdBeingEdited: LuisId | null;

    /**
     * Handler function for submitting the utterance edit operation
     */
    onSubmitEditInput: (text: string) => void;

    /**
     * Callback to set the edit mode
     */
    onEdit: (state: boolean) => void;

    /**
     * Handler function for the delete action
     */
    onDelete: () => void;

    /**
     * Callback function for confirm the utterance entity predictions
     */
    onConfirmPrediction: (utteranceId: LuisId) => void;

    /**
     * Wether the app's culture is RTL or not.
     */
    isRTL: boolean;

    /**
     * User Role to extract user actions.
     */
    userRole: string;
};

const messages = defineMessages({
    userInput: {
        id: 'newEntityDetails.entityUtterancesTable.userInput',
        defaultMessage: 'User input'
    },
    intentScore: {
        id: 'newEntityDetails.entityUtterancesTable.intentScore',
        defaultMessage: 'Intent and score'
    },
    emptyTablePlaceholder: {
        id: 'newEntityDetails.entityUtterancesTable.emptyTablePlaceholder',
        defaultMessage: 'There are no utterances found.'
    }
});

const { styleComponent } = getStylistV2('EntityUtterancesTable');

export const CellContainer = styleComponent<IStackProps>(Stack)('CellContainer', () => ({ marginTop: '27px' }));

const Row = styleComponent<IDetailsRowBaseProps>(DetailsRow)('Row', {
    minHeight: '55px',
    ...important({ background: 'transparent' }),
    $nest: { '&:hover': important({ background: 'transparent' }) }
});

/**
 * Represents a table that contains utterances for each intent
 */
const UtteranceTableBase = (props: EntityUtterancesTableProps) => {
    const {
        viewOptions,
        isLoading,
        items,
        onDelete,
        selection,
        isRTL,
        onEdit,
        utteranceIdBeingEdited,
        onSubmitEditInput,
        onConfirmPrediction,
        userRole,
        ...rest
    } = props;
    const triggerResize = useResizeTrigger();
    const debouncedTriggerSize = React.useCallback(debounce(triggerResize, 500), [triggerResize]);

    const { localize } = useLocalization();
    const { intents, isLoaded } = useIntents();

    const { canWrite } = mapUserRoleToUserActions(userRole);

    const columns: IColumn[] = [
        {
            key: 'utterance',
            name: localize(messages.userInput),
            fieldName: 'utterance',
            minWidth: 100,
            isRowHeader: true,
            isCollapsible: false,
            isMultiline: true,
            onColumnResize: debouncedTriggerSize,
            columnActionsMode: ColumnActionsMode.disabled,
            onRender: (utterance: UtterancePredictionsStore) => (
                <EntityUtteranceRow
                    viewOptions={viewOptions}
                    isRTL={isRTL}
                    key={utterance.utteranceStore.id.toString()}
                    utterancePredictionStore={utterance}
                    onDelete={onDelete}
                    onConfirmPrediction={() => onConfirmPrediction(utterance.utteranceStore.id)}
                    onEdit={onEdit}
                    isInEditMode={utteranceIdBeingEdited && utteranceIdBeingEdited === utterance.utteranceStore.id && canWrite}
                    onSubmitEditInput={onSubmitEditInput}
                    userRole={userRole}
                />
            )
        },
        {
            key: 'utteranceScore',
            name: localize(messages.intentScore),
            fieldName: 'score',
            minWidth: 130,
            isCollapsible: true,
            columnActionsMode: ColumnActionsMode.disabled,
            onRender: (utterance: UtterancePredictionsStore) => {
                const intentName = isLoaded && intents.find(({ id }) => utterance.utteranceStore.labeledIntentId === id)?.name;

                return <UtteranceScoreCell utterancePredictionStore={utterance} intentName={intentName} />;
            }
        }
    ];

    const onRenderRow = (rowProps: IDetailsRowProps) => <Row {...rowProps} />;

    return (
        <Table<UtterancePredictionsStore>
            getKey={(utterance: UtterancePredictionsStore) => utterance?.utteranceStore?.id.toString()}
            constrainMode={ConstrainMode.unconstrained}
            items={items}
            selection={selection}
            isLoading={isLoading}
            onRenderRow={onRenderRow}
            emptyPlaceholder={localize(messages.emptyTablePlaceholder)}
            columns={columns}
            {...rest}
        />
    );
};

/**
 * Wraps the utterance table with ResizeProvider that synchronizes resizing utterance
 * table column with the Labeling LayoutManager.
 */
export const EntityUtterancesTable = React.memo((props: EntityUtterancesTableProps) => (
    <ResizeProvider>
        <UtteranceTableBase {...props} />
    </ResizeProvider>
));

EntityUtterancesTable.displayName = 'EntityUtterancesTable';
